/**
 * OverrideDialog — Diálogo de override manual con justificación obligatoria.
 *
 * Requirements: M4.4.4
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface OverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (justificacion: string) => void;
  isLoading?: boolean;
}

export function OverrideDialog({ open, onOpenChange, onConfirm, isLoading }: OverrideDialogProps) {
  const { t } = useTranslation();
  const [justificacion, setJustificacion] = useState("");

  const handleConfirm = () => {
    if (!justificacion.trim()) return;
    onConfirm(justificacion.trim());
  };

  const handleOpenChange = (val: boolean) => {
    if (!val) setJustificacion("");
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("muestras.validacion.override_titulo")}</DialogTitle>
          <DialogDescription>{t("muestras.validacion.override_descripcion")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="justificacion">{t("muestras.validacion.justificacion")}</Label>
          <Textarea
            id="justificacion"
            rows={3}
            placeholder={t("muestras.validacion.justificacion_placeholder")}
            value={justificacion}
            onChange={(e) => setJustificacion(e.target.value)}
          />
          {justificacion.length === 0 && (
            <p className="text-xs text-destructive">
              {t("muestras.validacion.justificacion_requerida")}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isLoading}>
            {t("common.cancel")}
          </Button>
          <Button onClick={handleConfirm} disabled={!justificacion.trim() || isLoading}>
            {isLoading ? t("common.loading") : t("muestras.validacion.aplicar_override")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
